import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faRotate } from '@fortawesome/pro-solid-svg-icons';
import { isPast } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Position } from '@sorare/core/src/__generated__/globalTypes';
import {
  AverageLimit,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { Box } from '@sorare/core/src/atoms/ui/Box';
import { Dialog } from '@sorare/core/src/components/dialog';
import type { PrintablePlayerSortName } from '@sorare/core/src/contexts/config';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { range } from '@sorare/core/src/lib/arrays';

import { PlayerDrawer } from '@sorare/marketplace/src/components/player/PlayerDrawer';

import { AdvancedMarket } from '@sorare/gameplay/src/components/compose/AdvancedMarket';

import { PlayerRow } from './PlayerRow';
import { PlayerSearchInput } from './PlayerSearchInput';
import { SortControls } from './SortControls';
import type {
  EditFirstLineupPlayerQuery,
  EditFirstLineupPlayerQueryVariables,
  EditIndividualPlayerDialog_anyPlayer,
  EditIndividualPlayerDialog_so5Leaderboard,
  editablePlayerQuery,
  editablePlayerQueryVariables,
} from './__generated__/index.graphql';

const VerticalWithPadding = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--unit);
`;
const StyledDialog = styled(Dialog)`
  top: 0;
  padding: var(--unit);
  margin: 0 auto auto auto;
`;
const SkeletonContent = styled.div`
  height: 116px;
`;
const StyledBox = styled(Box)`
  border: 1px solid var(--c-rewards-pink);
  padding: 0 var(--double-unit) 0 0;
  background: rgba(255, 9, 116, 0.07);
`;

const playerFragment = gql`
  fragment EditIndividualPlayerDialog_anyPlayer on AnyPlayerInterface {
    slug
    anyCardWithLivePrimaryOffer(rarity: $rarity) {
      slug
    }
    ...PlayerRow_anyPlayerInterface
  }
  ${PlayerRow.fragments.anyPlayerInterface}
` as TypedDocumentNode<EditIndividualPlayerDialog_anyPlayer>;

const EDITABLE_PLAYER_QUERY = gql`
  query editablePlayerQuery(
    $so5LeaderboardSlug: String!
    $slug: String!
    $rarity: Rarity!
  ) {
    anyPlayer(slug: $slug) {
      slug
      ...EditIndividualPlayerDialog_anyPlayer
    }
  }
  ${playerFragment}
` as TypedDocumentNode<editablePlayerQuery, editablePlayerQueryVariables>;

const EDIT_FIRST_LINEUP_PLAYER_QUERY = gql`
  query EditFirstLineupPlayerQuery(
    $query: String!
    $page: Int
    $pageSize: Int
    $advancedFilters: String
    $sorts: [SearchSortInput!]!
    $refinements: [SearchRefinementInput!]!
    $facets: [SearchFacetDefinitionInput!]!
    $rarity: Rarity
    $so5LeaderboardSlug: String!
  ) {
    searchPlayers(
      query: $query
      page: $page
      pageSize: $pageSize
      sorts: $sorts
      advancedFilters: $advancedFilters
      refinements: $refinements
      facets: $facets
    ) {
      hits {
        id
        anyPlayer {
          slug
          ...EditIndividualPlayerDialog_anyPlayer
        }
      }
      ...AdvancedMarket_searchPlayers
    }
  }
  ${AdvancedMarket.fragments.searchPlayers}
  ${playerFragment}
` as TypedDocumentNode<
  EditFirstLineupPlayerQuery,
  EditFirstLineupPlayerQueryVariables
>;

type Props = {
  open: boolean;
  onClose: () => void;
  selectedPlayerSlug: string;
  selectedCardPositions: Position[];
  so5Leaderboard: EditIndividualPlayerDialog_so5Leaderboard;
  onSelectNewPlayer: ({
    cardSlug,
    playerSlug,
  }: {
    cardSlug: string;
    playerSlug: string;
  }) => void;
};
export const EditIndividualPlayerDialog = ({
  open,
  onClose,
  selectedPlayerSlug,
  selectedCardPositions,
  so5Leaderboard,
  onSelectNewPlayer,
}: Props) => {
  const { isAndroidApp, isIosApp, googlePlayStoreCountryCode } =
    useIsMobileApp();
  const l15Sort = 'DESC-L15' as const;
  const projectedGradeSort = 'DESC-projected_grade' as const;
  const [activeSort, setActiveSort] =
    useState<PrintablePlayerSortName>(l15Sort);
  const { data } = useQuery(EDITABLE_PLAYER_QUERY, {
    variables: {
      so5LeaderboardSlug: so5Leaderboard.slug,
      slug: selectedPlayerSlug,
      rarity: Rarity.limited,
    },
  });
  const [playerDetailsPlayer, setPlayerDetailsPlayer] =
    useState<EditIndividualPlayerDialog_anyPlayer | null>(null);

  const { anyPlayer } = data || {};

  const projectionAvailable =
    !!so5Leaderboard?.projectedLineupsReadyAt &&
    isPast(so5Leaderboard.projectedLineupsReadyAt);

  return (
    <>
      <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <VerticalWithPadding>
          {anyPlayer ? (
            <StyledBox
              labelProps={{
                color: 'var(--c-white)',
                bgColor: 'var(--c-rewards-pink)',
                value: (
                  <Horizontal gap={0.5}>
                    <FontAwesomeIcon icon={faRotate} />
                    <FormattedMessage
                      id="SwappedPlayer.boxTitle"
                      defaultMessage="Selected Player"
                    />
                  </Horizontal>
                ),
              }}
            >
              <PlayerRow
                player={anyPlayer}
                onClickStatsIcon={() => {
                  setPlayerDetailsPlayer(anyPlayer);
                }}
              />
            </StyledBox>
          ) : (
            <Skeleton loading>
              <SkeletonContent />
            </Skeleton>
          )}

          <AdvancedMarket
            filters={[
              `league_player_recommendations:${so5Leaderboard.so5League.slug}`,
            ]}
            positions={selectedCardPositions}
            query={EDIT_FIRST_LINEUP_PLAYER_QUERY}
            variables={{
              so5LeaderboardSlug: so5Leaderboard.slug,
              rarity: Rarity.limited,
              googlePlayStoreCountryCode,
              isAndroidApp,
              isIosApp,
              averageLimit: AverageLimit.LAST_15,
            }}
            sorts={['DESC-L15']}
            availableSorts={['DESC-L15', 'DESC-projected_grade']}
          >
            {({ players, loading: instantSearchLoading }) => {
              return (
                <Vertical>
                  <PlayerSearchInput />
                  {!!projectionAvailable && (
                    <SortControls
                      l15Sort={l15Sort}
                      projectedGradeSort={projectedGradeSort}
                      onSortChange={sort => {
                        setActiveSort(sort);
                      }}
                    />
                  )}
                  <Vertical gap={2}>
                    {!instantSearchLoading
                      ? players.map(player => {
                          return (
                            <PlayerRow
                              key={player.slug}
                              player={player}
                              onClickStatsIcon={() => {
                                setPlayerDetailsPlayer(player);
                              }}
                              onSelect={() => {
                                const cardSlug =
                                  player.anyCardWithLivePrimaryOffer?.slug;
                                if (cardSlug) {
                                  onSelectNewPlayer({
                                    cardSlug,
                                    playerSlug: player.slug,
                                  });
                                }
                                onClose();
                              }}
                              showProjection={activeSort === projectedGradeSort}
                              showAddButton
                            />
                          );
                        })
                      : range(5).map(i => (
                          <Skeleton key={i} loading>
                            <SkeletonContent />
                          </Skeleton>
                        ))}
                  </Vertical>
                </Vertical>
              );
            }}
          </AdvancedMarket>
        </VerticalWithPadding>
      </StyledDialog>
      <PlayerDrawer
        cardOpened={
          playerDetailsPlayer
            ? {
                playerSlug: playerDetailsPlayer.slug,
                cardSlug:
                  playerDetailsPlayer.anyCardWithLivePrimaryOffer?.slug || '',
              }
            : null
        }
        onClose={() => setPlayerDetailsPlayer(null)}
        onboarding
      />
    </>
  );
};

EditIndividualPlayerDialog.fragments = {
  so5Leaderboard: gql`
    fragment EditIndividualPlayerDialog_so5Leaderboard on So5Leaderboard {
      slug
      projectedLineupsReadyAt
      so5League {
        slug
      }
    }
  ` as TypedDocumentNode<EditIndividualPlayerDialog_so5Leaderboard>,
};
